/**
 * Presentational pieces for the Meeting Room Select — per-option row,
 * single-value trigger, and multi-select chip.
 *
 * Matches the Figma "Meeting room" recipe (`CustomRenderOptionWithGroups[Multi]`
 * in `@kula-ai/components/.../SelectVariations.stories.tsx`). Kept free of
 * any availability fetch logic — the caller (DetailsForm) assembles the
 * grouped options with `data: { status, capacity }` and passes these
 * renders down through the FormBuilder meeting-room config.
 */
import { Box, Flex, Icon } from '@chakra-ui/react';
import { Users01 } from '@kula-ai/icons/dual-tone/Users01';
import { XClose } from '@kula-ai/icons/outline/XClose';
import { CheckCircle } from '@kula-ai/icons-core';
import { Badge, Text, type SelectOption } from '@kula-ai/components';
import { KText } from '@/ui-library/components/text';
import type { MeetingRoomOptionData } from '@/formConfigs';

// `ChevronDown` was only used by the removed `renderMeetingRoomGroupHeader`.
// The default accordion header owns it now.

function getMeta(option: SelectOption): MeetingRoomOptionData | undefined {
  return option?.data as MeetingRoomOptionData | undefined;
}

function statusDotColor(status?: MeetingRoomOptionData['status']): string {
  return status === 'busy'
    ? 'var(--kula-colors-error-200)'
    : 'var(--kula-colors-success-200)';
}

/**
 * Per-row body for the accordion menu. The accordion's OptionRow
 * defers fully to `renderOption` when provided, so the right-side
 * check indicator that the default rendering would draw has to be
 * painted here too. We use the same `CheckCircle` icon kula-ai's
 * default option uses, so the indicator looks identical whether or
 * not the consumer supplies a custom row.
 */
export function renderMeetingRoomOption(
  option: SelectOption,
  { isSelected }: { isSelected: boolean }
) {
  const meta = getMeta(option);
  // kula's accordion row only paints its selected bg when NOT using a
  // custom `renderOption` (CustomAccordionMenu), so paint it here. The row
  // wrapper has `px-3 py-2.5` (12px / 10px) padding — negate it with
  // matching negative margins (and restore the padding) so the bg fills
  // the full row instead of just the content box.
  return (
    <Flex
      alignItems="flex-start"
      gap="3"
      w={isSelected ? 'calc(100% + 24px)' : '100%'}
      bg={isSelected ? 'secondary-25' : undefined}
      mx={isSelected ? '-3' : undefined}
      my={isSelected ? '-2.5' : undefined}
      px={isSelected ? '3' : undefined}
      py={isSelected ? '2.5' : undefined}
    >
      <Flex flex="1" minW={0} flexDirection="column" gap="1">
        <Flex alignItems="center" gap="2" minW={0}>
          {/* ENG-3763: `minW={0}` lets the name shrink — a flex item defaults
              to `min-width: auto` and refuses to go below its text, which
              widened the row and let the badge sit over the location. */}
          <Box flex="1" minW={0} title={meta?.name ?? option.label}>
            <Text variant="p" color="heading" truncate>
              {meta?.name ?? option.label}
            </Text>
          </Box>
          {/* Badge keeps its width; the name gives way instead. */}
          {meta?.status === 'available' ? (
            <Box flexShrink={0}>
              <Badge color="green">Available</Badge>
            </Box>
          ) : meta?.status === 'busy' ? (
            <Box flexShrink={0}>
              <Badge color="red">Busy</Badge>
            </Box>
          ) : null}
        </Flex>
        {/* Location on its own line: real room names and floors are both long
            enough that sharing a line left neither of them readable. */}
        {meta?.location ? (
          <Box minW={0} title={meta.location}>
            <Text variant="caption" color="muted" truncate>
              {meta.location}
            </Text>
          </Box>
        ) : null}
        {typeof meta?.capacity === 'number' && (
          <Flex alignItems="center" gap="1">
            <Icon as={Users01} w="12px" h="12px" color="secondary-500" />
            <Text variant="caption" color="heading">
              {meta.capacity}
            </Text>
          </Flex>
        )}
      </Flex>
      <Box
        w="18px"
        h="18px"
        flexShrink={0}
        // Sits on the name line — the row is three lines tall now, so centring
        // parked the tick next to the location instead.
        alignSelf="flex-start"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        {isSelected ? (
          <CheckCircle size={18} color="var(--kula-colors-primary-500)" />
        ) : null}
      </Box>
    </Flex>
  );
}

export function renderMeetingRoomTriggerValue(
  selected: SelectOption | SelectOption[] | null | undefined
) {
  const opt = Array.isArray(selected) ? selected[0] : selected;
  if (!opt) return null;
  const meta = getMeta(opt);
  const location = typeof meta?.location === 'string' ? meta.location : '';
  return (
    <Flex alignItems="center" gap="2" minW={0}>
      {meta?.status && (
        <Box
          w="8px"
          h="8px"
          flexShrink={0}
          borderRadius="full"
          bg={statusDotColor(meta.status)}
        />
      )}
      <KText variant="small_text" noOfLines={1} flexShrink={0}>
        {meta?.name ?? opt.label}
      </KText>
      {/* Location trails the name, muted; ellipsis + title tooltip when it
          overflows the trigger. */}
      {location && (
        <Box flex="1" minW={0} title={location}>
          <KText variant="small_text" color="secondary-500" noOfLines={1}>
            {location}
          </KText>
        </Box>
      )}
    </Flex>
  );
}

// Group header intentionally omitted — kula-ai's Select accordion
// variant already renders the office label + chevron + count-Tag
// header by default. Earlier we provided a custom `renderGroupHeader`
// that re-implemented all of this; that's now dead weight. Drop both
// the export and the `renderGroupHeader` consumer wiring so the
// shared component owns the look.

export function renderMeetingRoomTag(
  option: SelectOption,
  { onRemove }: { onRemove: () => void }
) {
  const meta = getMeta(option);
  return (
    <Flex
      as="span"
      alignItems="center"
      gap="2"
      m="1"
      px="2"
      py="0.5"
      borderRadius="md"
      border="1px solid"
      borderColor="secondary-100"
      bg="white"
    >
      {meta?.status && (
        <Box
          w="8px"
          h="8px"
          borderRadius="full"
          bg={statusDotColor(meta.status)}
        />
      )}
      <KText variant="small_text" color="secondary-700" noOfLines={1}>
        {meta?.name ?? option.label}
      </KText>
      <Box
        as="button"
        type="button"
        tabIndex={-1}
        onMouseDown={(e: React.MouseEvent) => {
          e.preventDefault();
          e.stopPropagation();
          onRemove();
        }}
        opacity={0.7}
        _hover={{ opacity: 1 }}
        display="inline-flex"
        cursor="pointer"
        aria-label={`Remove ${meta?.name ?? option.label}`}
      >
        <Icon as={XClose} w="12px" h="12px" />
      </Box>
    </Flex>
  );
}
